import { Autocomplete } from "@mabi-ui/autocomplete";
import { useQuery } from "@tanstack/react-query";
import { DocumentApi } from "certiblok-api-manager";
import { forwardRef, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { instanceApi } from "../../../hooks/api";
import { defaultQueryFilter, transformToStringFrom } from "../../tables/AdminTable";
import debounce from "lodash/debounce";
import { Spinner } from "@mabi-ui/spinner";
import BetterCheckbox from "../../BetterCheckbox";
import { CertiblokDocument } from "../../../interfaces/Document";
import {
	useIsPickerDocumentSelected,
	useTreeViewDocumentPickerContext,
} from "../controllers/TreeViewDocumentPickerContext";
import FileIcon from "../../icons/FileIcon";
import { backendMediaUrlParser } from "../../..";
import { Tooltip, Typography } from "@mui/material";
import { cn } from "@mabi-ui/utils";

const DocumentOption = forwardRef<HTMLDivElement, { value: CertiblokDocument; isActive: boolean }>(
	({ value, isActive, ...props }, ref) => {
		const isSelected = useIsPickerDocumentSelected(value);

		return (
			<div
				className={cn(
					"flex items-center gap-2 p-1 border-[2px] border-solid border-transparent rounded-xl hover:bg-secondary/10 cursor-pointer mb-[2px]",
					isActive && "border-secondary"
				)}
				ref={ref}
				{...props}
			>
				<BetterCheckbox disableRipple color="secondary" className="p-1" tabIndex={-1} checked={isSelected} readOnly />

				<div className="flex items-center gap-4 max-w-full w-full overflow-hidden">
					<FileIcon file={value.lastVersionFile?.[0]} dataUrl={backendMediaUrlParser(value?.lastVersionFile?.[0]?.url)} />
					<Tooltip title={value.name} enterDelay={500}>
						<Typography variant="body2" noWrap children={`${value.name}`} className="text-black" />
					</Tooltip>
				</div>
			</div>
		);
	}
);

export function TreeViewDocumentPickerAutocomplete({ className }: { className?: string }) {
	const { t } = useTranslation();
	const [search, setSearch] = useState("");
	const { selectedDocuments, setSelectedDocuments } = useTreeViewDocumentPickerContext();

	const throttledSetSearch = useCallback(
		debounce(
			(value: string) => {
				setSearch(value);
			},
			500,
			{ leading: false, trailing: true }
		),
		[]
	);

	const {
		data: allDocumentsQuery,
		isLoading: allDocumentsLoading,
		isFetching: allDocumentsFetching,
	} = useQuery({
		queryKey: ["document-picker", search],
		queryFn: () =>
			instanceApi(DocumentApi).listAllDocuments(
				transformToStringFrom({
					...defaultQueryFilter,
					orderBy: "name",
					order: "asc",
					page: 0,
					perPage: 10,
					filter: [
						{
							name: search,
						},
					],
				})
			),
		keepPreviousData: true,
	});

	return (
		<Autocomplete
			className={className}
			value={null}
			placeholder={t("allDocuments.searchAllDocuments")}
			aria-label={t("allDocuments.searchAllDocuments")}
			options={allDocumentsQuery?.data ?? []}
			nameExtractor={(option) => option?.name ?? ""}
			onInputChange={(value) => throttledSetSearch(value)}
			onChange={(value) => {
				if (!value) return;
				if (selectedDocuments.has(value?.id)) {
					setSelectedDocuments(Array.from(selectedDocuments.values()).filter((document) => document.id !== value?.id));
				} else {
					setSelectedDocuments([...Array.from(selectedDocuments.values()), value]);
				}
				setSearch("");
			}}
			color="secondary"
			TextFieldProps={{
				endIcon: allDocumentsFetching ? (
					<Spinner className="w-4 h-4 flex-shrink-0 text-secondary border-t-white border-[2px]" />
				) : null,
			}}
			renderOption={({ value, isActive }) => {
				return <DocumentOption value={value} isActive={isActive} />;
			}}
			OptionsListProps={{
				className: "z-[900000] shadow-8",
			}}
			emptyListContent={allDocumentsLoading ? t("global.loading") : t("search.noDocumentsFound")}
		/>
	);
}
